import { RouteComponentProps } from "../../../../../models/RouteComponent";
import useApiService from "../../../../../hooks/useApiService";
import { ChecklistRecord } from "../../../../../models/DSF/Equipment";
import React, { useEffect, useRef, useState } from "react";
import { CardActions, CardOverflow, Typography } from "@mui/joy";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CancelIcon, CheckIcon, ProgressActivity } from "../../../../../assets/icons";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import "../shared/styles.scss";
import { displayContent } from "../../../../../utils/generalUtils";
import { formatCurrency } from "../../../../../utils/currenctUtils";
import { formatDate } from "../../../../../utils/DateUtils";
import { CompanyDocumentRecord } from "../../../../../models/DSF/CompanyDocuments";
import { VehicleRecord } from "../../../../../models/DSF/Vehicle";
import Documents from "../shared/Documents";

function VehicleListAndChecklist(props: RouteComponentProps) {
  const { session } = props;
  const [vehicleId, _] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [checklist, setChecklist] = useState<ChecklistRecord | null>(null);
  const [documents, setDocuments] = useState<CompanyDocumentRecord[]>([]);

  // TODO: Temp fix until equipment details endpoint returns correct response
  const [vehicleData, setVehicleData] = useState<VehicleRecord | null>(null);

  const toast = useRef<any>(null);

  const { data, error, loading } = useApiService<VehicleRecord[]>({
    method: "GET",
    url: "site-vehicles",
    apiName: "open",
    params: {
      siteId: session.site_id,
      clientId: session.client_id,
    },
  });

  const {
    fetchData: fetchChecklists,
    loading: loadingChecklist,
    error: checklistError,
  } = useApiService<ChecklistRecord>({
    url: `site-vehicles/${vehicleId}/checklists`,
    fetchOnMount: false,
    method: "GET",
    apiName: "open",
    params: {
      siteId: session.site_id,
      clientId: session.client_id,
    },
    onSuccess: data => {
      setChecklist(data);
    },
  });

  const {
    fetchData: fetchDocuments,
    loading: loadingDocuments,
    error: errorDocuments,
  } = useApiService<CompanyDocumentRecord[]>({
    url: `site-vehicles/${vehicleId}/documents`,
    method: "GET",
    apiName: "open",
    params: {
      siteId: session.site_id,
      clientId: session.client_id,
    },
    fetchOnMount: false,
    onSuccess: data => {
      setDocuments(data);
    },
  });

  useEffect(() => {
    props.setGlobalLoader(loading);
  }, [loading]);

  const handleTabChange = async (e: any) => {
    setActiveIndex(e.index);
    if (e.index === 1 && !checklist) {
      fetchChecklists({ url: `site-vehicles/${vehicleData?.id}/checklists` });
    }
    if (e.index === 2 && documents.length === 0) {
      fetchDocuments({ url: `site-vehicles/${vehicleData?.id}/documents` });
    }
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Vehicle list & checklist
      </Typography>
      <CardOverflow
        style={{ width: "100%" }}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      >
        <CardActions
          style={{ marginTop: 15, marginBottom: 15 }}
          sx={{ alignSelf: "flex-end", pt: 2 }}
        >
          <Button
            name="button"
            icon="pi pi-save"
            label="Download PDF"
            className="p-button-outlined"
            size="small"
            disabled={true}
            style={{
              position: "absolute",
              fontSize: "small",
              right: 0,
              color: "#2dd36f",
            }}
            onClick={() => {}}
          />
        </CardActions>
      </CardOverflow>
      {error ? (
        <div className={"error-wrapper"}>
          <h2>Error fetching tool list</h2>
        </div>
      ) : (
        <>
          {data && (
            <DataTable
              value={data}
              onRowClick={e => {
                setVehicleData(e.data as VehicleRecord);
              }}
              className={"policies-procedures-table"}
            >
              <Column field={"name"} header={"Name"} />
              <Column field={"description"} header={"Description"} />
              <Column field={"vehicle_type_name"} header={"Vehicle type"} />
              <Column field={"vin_number"} header={"Vin number"} />
            </DataTable>
          )}
        </>
      )}
      {vehicleData && (
        <Dialog
          onHide={() => {
            setVehicleData(null);
            setChecklist(null);
            setActiveIndex(0);
            setDocuments([]);
          }}
          visible={!!vehicleData}
          header={vehicleData.name}
          style={{ width: "60vw" }}
        >
          <TabView onTabChange={handleTabChange} activeIndex={activeIndex}>
            <TabPanel header={"Equipment details"} key={"details"}>
              <div className={"equipment-leading-content shadow"}>
                <img
                  className={"equipment-photo"}
                  alt="Silhouette of mountains"
                  src={
                    vehicleData?.photo || "https://ionicframework.com/docs/img/demos/card-media.png"
                  }
                  onError={e => {
                    e.currentTarget.src =
                      "https://ionicframework.com/docs/img/demos/card-media.png";
                  }}
                />

                <div>
                  <div className={"key-value"}>
                    <label className={"font-bold mr-4"}>Serial:</label>
                    <span>{displayContent(vehicleData.vin_number)}</span>
                  </div>
                  <div className={"key-value"}>
                    <label className={"font-bold mr-4"}>Price:</label>
                    <span>{formatCurrency(vehicleData.price)}</span>
                  </div>
                  <div className={"key-value"}>
                    <label className={"font-bold mr-4"}>Description:</label>
                    <span>{displayContent(vehicleData.description)}</span>
                  </div>
                </div>
              </div>
              <div className={"shadow maintenance-content"}>
                <div className={"maintenance-section-header maintenance-section-header-darker"}>
                  Maintenance
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Maintenance interval</p>
                  <p>{displayContent(vehicleData.maintenance_interval)}</p>
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Maintenance start date</p>
                  <p>{formatDate(vehicleData.maintenance_start_date)}</p>
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Maintenance value</p>
                  <p>{formatCurrency(vehicleData.maintenance_value)}</p>
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Next maintenance window</p>
                  <p>{formatDate(vehicleData.next_maintenance_window)}</p>
                </div>
              </div>
              <div className={"shadow maintenance-content"}>
                <div className={"maintenance-section-header maintenance-section-header-darker"}>
                  Vehicle details
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Vehicle type</p>
                  <p>{displayContent(vehicleData.vehicle_type_name)}</p>
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Expected life interval</p>
                  <p>{displayContent(vehicleData.expected_life_interval)}</p>
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Expected life value</p>
                  <p>{formatCurrency(vehicleData.expected_life_value)}</p>
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Expected life window</p>
                  <p>{formatDate(vehicleData.expected_life_window)}</p>
                </div>
              </div>
            </TabPanel>
            <TabPanel header={"Checklists"} key={"checklists"}>
              {checklistError ? (
                <div className={"error-wrapper"}>
                  <h2>Error fetching checklists</h2>
                </div>
              ) : (
                <>
                  {loadingChecklist ? (
                    <div className={"loading-wrapper"}>
                      <ProgressActivity className={"spinner"} />
                    </div>
                  ) : (
                    <TabView>
                      <TabPanel header={"Risks associated"}>
                        <div className={"checklist-wrapper"}>
                          {checklist?.risk.length === 0 ? (
                            <>
                              <p>
                                There are no questions found for the risks associated with{" "}
                                {vehicleData.name}
                              </p>
                            </>
                          ) : (
                            <>
                              <p>
                                Read through the questions about the risks associated with the{" "}
                                {vehicleData.name}
                              </p>
                              <div className={"responsive-grid-2 checklist-table-header"}>
                                <p>Question</p>
                                <p>Active</p>
                              </div>
                              {checklist?.risk.map((item, index) => {
                                return (
                                  <div className={"responsive-grid-2"} key={index}>
                                    <p>{item.question}</p>
                                    <p>
                                      {item.active ? (
                                        <CheckIcon fill={"#2dd36f"} />
                                      ) : (
                                        <CancelIcon className={"cancel-icon"} />
                                      )}
                                    </p>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </TabPanel>
                      <TabPanel header={"Safety practices"}>
                        <div className={"checklist-wrapper"}>
                          {checklist?.safe.length === 0 ? (
                            <>
                              <p>There are no safety questions found for {vehicleData.name}</p>
                            </>
                          ) : (
                            <>
                              <p>
                                Read through the safety question before using the {vehicleData.name}
                              </p>
                              <div className={"responsive-grid-2 checklist-table-header"}>
                                <p>Question</p>
                                <p>Active</p>
                              </div>
                              {checklist?.safe.map((item, index) => {
                                return (
                                  <div className={"responsive-grid-2"} key={index}>
                                    <p>{item.question}</p>
                                    <p>
                                      {item.active ? (
                                        <CheckIcon fill={"#2dd36f"} />
                                      ) : (
                                        <CancelIcon className={"cancel-icon"} />
                                      )}
                                    </p>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </TabPanel>
                    </TabView>
                  )}
                </>
              )}
            </TabPanel>
            <TabPanel header={"Documents"} key={"documents"}>
              <Documents
                documents={documents}
                loadingDocuments={loadingDocuments}
                errorDocuments={errorDocuments}
                data={vehicleData}
              />
            </TabPanel>
          </TabView>
        </Dialog>
      )}
      <Toast ref={toast} />
    </>
  );
}

export default VehicleListAndChecklist;
